import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { RouterLink } from '@angular/router';
import { PublicHeroBannerData } from '../../data/home-blocks.model';
import { resolveHomeBannerUrl } from '../../../../shared/utils/home-blocks.utils';

/**
 * Bloque del home asociado al type `hero-banner` del API: un banner único
 * mid-page (NO el carrusel del top, que es otro endpoint — `/api/hero-carousel`
 * con `HeroComponent`). Pinta eyebrow + título + subtítulo + botón opcional
 * sobre una imagen de fondo a ancho completo con overlay para legibilidad.
 *
 * El backend ya omite el bloque si faltan `title` o `background_image.imageUrl`;
 * aquí defendemos contra inputs nulos por si la respuesta llega malformada.
 */
@Component({
  selector: 'app-hero-banner-block',
  imports: [NgOptimizedImage, RouterLink],
  templateUrl: './hero-banner-block.component.html',
  styleUrl: './hero-banner-block.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroBannerBlockComponent {
  readonly data = input<PublicHeroBannerData | null>(null);

  protected readonly eyebrow = computed(() => this.data()?.eyebrow ?? '');
  protected readonly title = computed(() => this.data()?.title ?? '');
  protected readonly subtitle = computed(() => this.data()?.subtitle ?? '');
  protected readonly button = computed(() => this.data()?.button ?? null);
  protected readonly imageUrl = computed(() =>
    resolveHomeBannerUrl(this.data()?.background_image),
  );
  protected readonly imageAlt = computed(
    () => this.data()?.background_image?.alt ?? this.title(),
  );

  protected readonly shouldRender = computed(
    () => !!this.data() && !!this.title() && !!this.imageUrl(),
  );

  // El button.url puede ser absoluto (`https://...`) o relativo SPA (`/tienda`).
  // Discriminamos para usar `<a href>` externo vs `[routerLink]` interno.
  protected readonly isExternalButton = computed(() => {
    const url = this.button()?.url ?? '';
    return /^https?:\/\//i.test(url);
  });
}
